import type { Client } from "@coachapp/shared";
import { Calendar, MessageSquare, Tag, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../lib/api-error";
import { toDateValue } from "../lib/datetime-local";
import { useUpdateClientMutation } from "../store/coachApi";
import DatePicker from "./DatePicker";
import ErrorAlert from "./ErrorAlert";

interface ClientCrmPanelProps {
  client: Client;
  onUpdate: (updated: Client) => void;
}

const toDateInputValue = (value?: string) =>
  value ? toDateValue(value) : "";

const ClientCrmPanel = ({
  client: initialClient,
  onUpdate,
}: ClientCrmPanelProps) => {
  const { t } = useTranslation();
  const [updateClient] = useUpdateClientMutation();
  const [crmNotes, setCrmNotes] = useState(initialClient.crmNotes ?? "");
  const [tags, setTags] = useState<string[]>(initialClient.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [nextActionAt, setNextActionAt] = useState(
    toDateInputValue(initialClient.nextActionAt),
  );
  const [nextActionNote, setNextActionNote] = useState(
    initialClient.nextActionNote ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [markingContact, setMarkingContact] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCrmNotes(initialClient.crmNotes ?? "");
    setTags(initialClient.tags ?? []);
    setNextActionAt(toDateInputValue(initialClient.nextActionAt));
    setNextActionNote(initialClient.nextActionNote ?? "");
  }, [
    initialClient.id,
    initialClient.crmNotes,
    initialClient.tags,
    initialClient.nextActionAt,
    initialClient.nextActionNote,
  ]);

  const commitTagInput = (currentTags: string[], input: string) => {
    const trimmed = input.trim();
    if (!trimmed || currentTags.includes(trimmed)) {
      return currentTags;
    }
    return [...currentTags, trimmed];
  };

  const addTag = (inputValue: string = tagInput) => {
    setTags((current) => commitTagInput(current, inputValue));
    setTagInput("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  const buildPayload = (tagsToSave: string[]) => ({
    crmNotes: crmNotes || null,
    tags: tagsToSave,
    nextActionAt: nextActionAt ? new Date(nextActionAt).toISOString() : null,
    nextActionNote: nextActionNote || null,
  });

  const handleSave = async () => {
    const tagsToSave = commitTagInput(tags, tagInput);
    setTags(tagsToSave);
    setTagInput("");

    setSaving(true);
    try {
      const updated = await updateClient({
        id: initialClient.id,
        data: buildPayload(tagsToSave) as Partial<Client>,
      }).unwrap();
      setCrmNotes(updated.crmNotes ?? "");
      setTags(updated.tags ?? []);
      setNextActionAt(toDateInputValue(updated.nextActionAt));
      setNextActionNote(updated.nextActionNote ?? "");
      onUpdate(updated);
    } catch (err) {
      setError(
        err &&
          typeof err === "object" &&
          "message" in err &&
          typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось сохранить CRM данные"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkContact = async () => {
    setMarkingContact(true);
    try {
      const updated = await updateClient({
        id: initialClient.id,
        data: { lastContactAt: new Date().toISOString() },
      }).unwrap();
      onUpdate(updated);
    } catch (err) {
      setError(
        err &&
          typeof err === "object" &&
          "message" in err &&
          typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось отметить контакт"),
      );
    } finally {
      setMarkingContact(false);
    }
  };

  return (
    <div className="card p-5 md:p-6">
      <h2 className="text-lg font-bold text-slate-900 mb-5">{t("CRM")}</h2>

      {error && (
        <ErrorAlert
          message={error}
          onDismiss={() => setError(null)}
          className="mb-4"
        />
      )}

      <div className="space-y-5">
        <div>
          <label className="flex items-center gap-2 text-slate-400 mb-2">
            <MessageSquare size={14} />
            <span className="section-label">
              {t("Заметки")}
            </span>
          </label>
          <textarea
            className="input-field min-h-[100px] resize-y"
            value={crmNotes}
            onChange={(e) => setCrmNotes(e.target.value)}
            placeholder={t("Заметки")}
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-slate-400 mb-2">
            <Tag size={14} />
            <span className="section-label">
              {t("Теги")}
            </span>
          </label>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-soft text-primary rounded-full text-sm font-medium"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => setTags(tags.filter((t) => t !== tag))}
                    className="hover:text-primary-dark transition-colors"
                    aria-label={t("Удалить тег")}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              className="input-field flex-1"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={(e) => addTag(e.currentTarget.value)}
              placeholder={t("Добавить тег")}
            />
            <button
              type="button"
              onClick={() => addTag()}
              disabled={!tagInput.trim()}
              className="btn-secondary text-sm px-4 shrink-0"
            >
              {t("Добавить")}
            </button>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-slate-400 mb-2">
            <Calendar size={14} />
            <span className="section-label">
              {t("Следующее действие")}
            </span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DatePicker
              value={nextActionAt}
              onChange={setNextActionAt}
              clearable
            />
            <input
              type="text"
              className="input-field"
              value={nextActionNote}
              onChange={(e) => setNextActionNote(e.target.value)}
              placeholder={t("Описание действия")}
            />
          </div>
        </div>

        {initialClient.lastContactAt && (
          <p className="text-sm text-slate-500">
            {t("Последний контакт")}:{" "}
            <span className="font-medium text-slate-700">
              {new Date(initialClient.lastContactAt).toLocaleDateString(
                undefined,
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                },
              )}
            </span>
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            type="button"
            onClick={handleMarkContact}
            disabled={markingContact}
            className="btn-secondary text-sm py-2"
          >
            {markingContact
              ? t("Сохранение...")
              : t("Отметить контакт сегодня")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm py-2 sm:ml-auto"
          >
            {saving ? t("Сохранение...") : t("Сохранить")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientCrmPanel;
